import { Base } from '@studiometa/js-toolkit';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

gsap.registerPlugin(ScrollTrigger);

/**
 * GalleryScale component
 */
export default class GalleryScale extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'GalleryScale',
    refs: ['media[]', 'container', 'slide[]'],
  };

  /**
   *  mounted hook
   */
  mounted() {
    const totalImages = this.$refs.media.length;
    const container = this.$refs.container;

    gsap.set(this.$refs.slide, { force3D: true, willChange: 'transform' });
    gsap.set(this.$refs.media, { force3D: true, willChange: 'clip-path' });

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: container.parentElement,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 3,
      },
    });

    const translateDuration = 32; // Durée de l'animation du translate X
    const clipPathDuration = 6; // Durée de l'animation du clipPath
    const gapDuration = 8; // Durée de pause entre chaque slide

    this.$refs.slide.forEach((slide, slideIndex) => {
      const media = this.$refs.media[slideIndex];

      if (slideIndex === 0) {
        gsap.set(slide, {
          x: '0%',
          zIndex: 1,
        });

        mainTimeline.fromTo(
          media,
          {
            clipPath: 'inset(15% 10% 15% 10%)',
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: clipPathDuration,
            ease: 'none',
          },
          0,
        );
      } else if (slideIndex === totalImages - 1) {
        gsap.set(slide, {
          x: '100%',
          zIndex: totalImages,
        });

        const startTime = slideIndex * (translateDuration + gapDuration);

        mainTimeline.fromTo(
          slide,
          {
            x: '100%',
          },
          {
            x: '0%',
            duration: translateDuration,
            ease: 'none',
          },
          startTime,
        );

        mainTimeline
          .fromTo(
            media,
            {
              clipPath: 'inset(15% 10% 15% 10%)',
            },
            {
              clipPath: 'inset(0% 0% 0% 0%)',
              duration: clipPathDuration,
              ease: 'none',
            },
            startTime + translateDuration,
          )
          .to(
            media,
            {
              clipPath: 'inset(15% 10% 15% 10%)',
              duration: clipPathDuration,
              ease: 'none',
            },
            startTime + translateDuration + gapDuration * 2,
          );
      } else {
        // Slides intermédiaires : commencent hors écran à droite
        let startTime;

        if (slideIndex === 1) {
          startTime = slideIndex * (translateDuration / 2 + gapDuration);
        } else {
          startTime = slideIndex * (translateDuration + gapDuration);
        }

        gsap.set(slide, {
          x: '100%',
          zIndex: slideIndex + 1,
        });

        mainTimeline.fromTo(
          slide,
          {
            x: '100%',
          },
          {
            x: '0%',
            duration: translateDuration,
            ease: 'none',
          },
          startTime,
        );

        mainTimeline.fromTo(
          media,
          {
            clipPath: 'inset(15% 10% 15% 10%)',
          },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: clipPathDuration,
            ease: 'none',
          },
          startTime + translateDuration,
        );
      }
    });

    this.refreshWhenPageIsReady();
  }

  /**
   * Recalcule les bornes du ScrollTrigger une fois la page entièrement chargée.
   *
   * Le composant est monté très tôt (import statique dans le registre des sections).
   * Les sections situées au-dessus de la galerie peuvent encore grandir ensuite,
   * le temps que leurs médias et leurs polices se chargent : la position de la
   * section change alors, mais les bornes calculées au montage, elles, ne bougent
   * pas. La timeline reste figée sur son état initial pendant tout le scroll.
   */
  refreshWhenPageIsReady() {
    if (document.readyState === 'complete') {
      ScrollTrigger.refresh();
      return;
    }

    window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  }

  /**
   * Recalcule les bornes au changement de taille du viewport.
   */
  resized() {
    ScrollTrigger.refresh();
  }
}
